import {inject, Injectable, Signal, signal, WritableSignal} from '@angular/core';
import {AppService} from '../app/app.service';
import {DialogService} from '../dialog/dialog.service';
import {AiChatMessage} from '../../models/ai/ai-chat-message';
import {AiChatDocument} from '../../models/ai/ai-chat-document';
import {TranslateService} from '@ngx-translate/core';
import {ConversationService as BfaConversationService} from '../../bfa-api/services/conversation.service';
import {firstValueFrom, lastValueFrom} from 'rxjs';
import {HttpClient, HttpContext, HttpErrorResponse, HttpEvent, HttpEventType, HttpHeaders} from '@angular/common/http';
import {SendChatMessageRequest} from '../../ai-api/models/send-chat-message-request';
import {marked} from 'marked';
import {LOCAL_FILE_SERVICE, LocalFileService} from '../local-file/local-file.service';
import {ConversationWithDocumentsCreationData} from 'src/app/bfa-api/models/conversation-with-documents-creation-data';
import {AiChatMessageTypeEnum} from '../../enums/ai-chat/ai-chat-message-type.enum';
import {PromptSuggestion} from '../../bfa-api/models/prompt-suggestion';
import {environment} from '../../../environments/environment';
import {AiChat} from '../../models/ai/ai-chat';
import {AUTH_TOKEN_HTTP_CONTEXT} from '../../constants/auth-token-http-context.constant';
import {AuthService} from '../auth/auth.service';
import {ConversationWithVaultsCreationData} from '../../bfa-api/models/conversation-with-vaults-creation-data';
import {Vault} from '../../api/models/vault';
import {ConversationWithOrganisationCreationData} from '../../bfa-api/models/conversation-with-organisation-creation-data';
import {AiChatContextEnum} from '../../enums/ai-chat/ai-chat-context.enum';
import {ChatDocument} from '../../ai-api/models/chat-document';
import {SentryService} from '../sentry/sentry.service';
import {ShareResult} from '@capacitor/share';
import {ApiErrorMappingService} from '../mapping/api-error-mapping/api-error-mapping.service';
import {Conversation} from '../../bfa-api/models/conversation';
import {PanelLocationEnum} from '../../enums/panel-location.enum';

@Injectable({
    providedIn: 'root'
})
export class AiChatService {
    private readonly isShowingAiChat: WritableSignal<boolean>;
    private readonly aiChatSignal: WritableSignal<AiChat>;
    private currentChatVault: Vault | undefined;
    private readonly apiErrorMappingService = inject(ApiErrorMappingService);
    private readonly appService = inject(AppService);
    private readonly authService = inject(AuthService);
    private readonly bfaConversationService = inject(BfaConversationService);
    private readonly dialogService = inject(DialogService);
    private readonly httpClient = inject(HttpClient);
    private readonly localFileService = inject<LocalFileService>(LOCAL_FILE_SERVICE);
    private readonly sentryService = inject(SentryService);
    private readonly translateService = inject(TranslateService);

    public constructor() {
        this.aiChatSignal = signal({
            aiApiHostUrl: '',
            conversationName: '',
            conversationId: '',
            chatDocuments: [],
            chatMessages: [],
            isGeneratingMessage: false,
            promptSuggestions: [],
            aiChatContext: AiChatContextEnum.documents
        });
        this.isShowingAiChat = signal(false);
    }

    public getIsShowingAiChatSignal(): Signal<boolean> {
        return this.isShowingAiChat.asReadonly();
    }

    public getIsShowingAiChat(): boolean {
        return this.isShowingAiChat();
    }

    // Possible BackendForAppErrorCodes: NotAuthorized, AiServicesNotEnabled, NoReadableDocuments
    public async setupAiChatWithDocuments(documents: Array<AiChatDocument>): Promise<void> {
        const documentIds = documents.map(doc => doc.documentId);
        const documentNames = documents.map(doc => doc.documentName);
        const conversationName: string = this.translateService.instant('AI_CHAT.DOCUMENT.TITLE', { documentNames: [documentNames] });
        const conversationWithDocumentsCreationData: ConversationWithDocumentsCreationData = {
            conversationName: conversationName,
            documentIds: documentIds,
        };
        const token = await this.authService.getToken();

        let conversation: Conversation;
        try {
            conversation = await firstValueFrom(this.bfaConversationService.conversationStartConversationWithDocuments(
                {
                    body: conversationWithDocumentsCreationData
                },
                new HttpContext().set(AUTH_TOKEN_HTTP_CONTEXT, token)
            ));
        } catch (error) {
            throw this.apiErrorMappingService.mapBfaError(error);
        }

        this.updateAiChat({
            conversationName,
            conversationId: conversation.conversationId,
            aiApiHostUrl: conversation.aiApiHostUrl,
            chatDocuments: documents,
            aiChatContext: AiChatContextEnum.documents
        });

        const welcomeMessage = this.getDocumentsWelcomeMessage(documentIds);
        await this.addFullMessageToChat(welcomeMessage, AiChatMessageTypeEnum.aiMessage);
        this.showAiChatOverlay();
    }

    // Possible BackendForAppErrorCodes: NotAuthorized, AiServicesNotEnabled
    public async setupAiChatWithVault(vault: Vault): Promise<void> {
        const conversationName = this.translateService.instant('AI_CHAT.VAULT.TITLE', { vaultName: [vault.name] });
        const conversationWithVaultsCreationData: ConversationWithVaultsCreationData = {
            conversationName: conversationName,
            vaultIds: [vault.id],
        };
        const token = await this.authService.getToken();

        let conversation: Conversation;
        try {
            conversation = await firstValueFrom(this.bfaConversationService.conversationStartConversationWithVaults(
                {
                    body: conversationWithVaultsCreationData
                },
                new HttpContext().set(AUTH_TOKEN_HTTP_CONTEXT, token)
            ));
        } catch (error) {
            throw this.apiErrorMappingService.mapBfaError(error);
        }

        this.updateAiChat({
            conversationName,
            conversationId: conversation.conversationId,
            aiApiHostUrl: conversation.aiApiHostUrl,
            aiChatContext: AiChatContextEnum.vaults
        });
        this.currentChatVault = vault;

        const welcomeMessage = this.translateService.instant('AI_CHAT.VAULT.FIRST_PROMPT');
        await this.addFullMessageToChat(welcomeMessage, AiChatMessageTypeEnum.aiMessage);
        this.showAiChatOverlay();
    }

    // Possible BackendForAppErrorCodes: AiServicesNotEnabled
    public async setupAiChatWithOrganisation(): Promise<void> {
        const conversationName = this.translateService.instant('AI_CHAT.ORGANISATION.TITLE');
        const conversationWithOrganisationCreationData: ConversationWithOrganisationCreationData = {
            conversationName: conversationName,
        };
        const token = await this.authService.getToken();

        let conversation: Conversation;
        try {
            conversation = await firstValueFrom(this.bfaConversationService.conversationStartConversationWithOrganisation(
                {
                    body: conversationWithOrganisationCreationData
                },
                new HttpContext().set(AUTH_TOKEN_HTTP_CONTEXT, token)
            ));
        } catch (error) {
            throw this.apiErrorMappingService.mapBfaError(error);
        }

        this.updateAiChat({
            conversationName,
            conversationId: conversation.conversationId,
            aiApiHostUrl: conversation.aiApiHostUrl,
            aiChatContext: AiChatContextEnum.organisation
        });

        const welcomeMessage = this.translateService.instant('AI_CHAT.ORGANISATION.FIRST_PROMPT');
        await this.addFullMessageToChat(welcomeMessage, AiChatMessageTypeEnum.aiMessage);
        this.showAiChatOverlay();
    }

    public async submitChatMessage(prompt: string): Promise<void> {
        await this.addFullMessageToChat(prompt, AiChatMessageTypeEnum.userPrompt);

        const aiChatContext = this.aiChatSignal().aiChatContext;
        const suspendSearch = aiChatContext === AiChatContextEnum.documents;
        await this.generateChatResponse(prompt, [], suspendSearch);
    }

    public async getPromptSuggestions(): Promise<PromptSuggestion[]> {
        if (this.aiChatSignal().promptSuggestions.length > 0) {
            return this.aiChatSignal().promptSuggestions;
        }

        const documentIds: string[] = [];
        this.aiChatSignal()
            .chatDocuments
            .forEach(document => documentIds.push(document.documentId));
        const token = await this.authService.getToken();
        const promptSuggestions =
            (await lastValueFrom(this.bfaConversationService.conversationGetPromptSuggestionList(
                {
                    conversationId: this.aiChatSignal().conversationId ?? '',
                    body: { documentIds: documentIds }
                },
                new HttpContext().set(AUTH_TOKEN_HTTP_CONTEXT, token)
            ))).items;

        this.updateAiChat({ promptSuggestions });
        return promptSuggestions;
    }

    public async hideAiChatOverlay(skipConfirmation: boolean = false): Promise<boolean> {
        if (!this.isShowingAiChat()) {
            this.resetChat();
            return true;
        }

        if (!skipConfirmation) {
            const confirmed = await this.dialogService.showConfirmDialog({
                messageTranslationKey: 'AI_CHAT.CONFIRM_CLOSING',
                actionTranslationKey: 'BUTTON.END',
            }, 'ai-chat-confirm-closing', {
                shouldCloseOnBackdropClick: false,
                shouldCloseOnEscapeKey: false,
                shouldCloseOnNavigation: false
            });

            if (!confirmed) {
                return false;
            }
        }

        this.appService.setShowingSmallMenu(PanelLocationEnum.Content, false);
        this.isShowingAiChat.set(false);
        this.resetChat();
        return true;
    }

    public async shareLastAiMessage(): Promise<void | ShareResult> {
        const lastAiMessage = this.aiChatSignal()
            .chatMessages
            .findLast(chatMessage => chatMessage().messageType == AiChatMessageTypeEnum.aiMessage);

        return await this.localFileService.shareUrl(lastAiMessage?.().markdownMessage ?? '');
    }

    public getAiChatSignal(): Signal<AiChat> {
        return this.aiChatSignal.asReadonly();
    }

    // Possible BackendForAppErrorCodes: NotAuthorized, AiServicesNotEnabled, NoReadableDocuments
    public async clearChat(): Promise<void> {
        this.updateAiChat({
            conversationId: '',
            aiApiHostUrl: '',
            chatMessages: [],
            isGeneratingMessage: false,
        });
        await this.restartCurrentChat();
    }

    private showAiChatOverlay(): void {
        this.appService.setShowingSmallMenu(PanelLocationEnum.Content, false);
        this.isShowingAiChat.set(true);
    }

    private async restartCurrentChat(): Promise<void> {
        switch (this.aiChatSignal().aiChatContext) {
            case AiChatContextEnum.vaults:
                this.updateAiChat({ chatDocuments: [] });
                await this.setupAiChatWithVault(this.currentChatVault!);
                break;
            case AiChatContextEnum.organisation:
                this.updateAiChat({ chatDocuments: [] });
                await this.setupAiChatWithOrganisation();
                break;
            case AiChatContextEnum.documents:
                await this.setupAiChatWithDocuments(this.aiChatSignal().chatDocuments);
                break;
            default:
                break;
        }
    }

    private async generateChatResponse(prompt: string, excludedContentIds: string[] = [], suspendSearch: boolean = true): Promise<void> {
        this.updateAiChat({ isGeneratingMessage: true });
        const generatedChatResponse: WritableSignal<AiChatMessage> = signal({
            markdownMessage: '',
            formattedHtmlMessage: '',
            messageType: AiChatMessageTypeEnum.aiMessage
        });
        this.aiChatSignal.update(chat => {
            chat.chatMessages.push(generatedChatResponse);
            return chat;
        });

        const sendChatMessageRequest: SendChatMessageRequest = {
            messageText: prompt,
            excludedContentIds: excludedContentIds,
            suspendSearch: suspendSearch
        };
        const aiConversationsChatRoute = '/conversations/' + this.aiChatSignal().conversationId + '/chat';

        const headers = new HttpHeaders({
            'Authorization': await this.authService.getBearer()
        });
        this.httpClient.post(this.aiChatSignal().aiApiHostUrl + aiConversationsChatRoute, sendChatMessageRequest, {
                headers,
                observe: 'events',
                responseType: 'text',
                reportDownloadProgress: true,
            })
            .subscribe({
                next: async (event: HttpEvent<string>) => {
                    if (event.type === HttpEventType.DownloadProgress) {
                        const markdownMessage = this.replaceDocumentNamesAndIds((event).partialText ?? '');
                        const formattedHtmlMessage = await this.parseMarkdown(markdownMessage);
                        generatedChatResponse.update(response => {
                            return {
                                ...response,
                                markdownMessage,
                                formattedHtmlMessage
                            };
                        });
                    } else if (event.type === HttpEventType.Response) {
                        if (this.aiChatSignal().aiChatContext !== AiChatContextEnum.documents) {
                            await this.fetchCurrentChatDocuments();
                        }

                        const markdownMessage = this.replaceDocumentNamesAndIds(event.body ?? '');
                        const formattedHtmlMessage = await this.parseMarkdown(markdownMessage);
                        generatedChatResponse.update(response => {
                            return {
                                ...response,
                                markdownMessage,
                                formattedHtmlMessage
                            };
                        });
                        this.updateAiChat({ isGeneratingMessage: false });
                    }
                },
                error: async (error) => {
                    // Error is handled here to send the error message like a typical AI response.
                    let errorMessage: string;
                    if (error instanceof HttpErrorResponse) {
                        switch (error.status) {
                            case 402:
                                errorMessage = this.translateService.instant('AI_CHAT.AI_QUOTA_TOO_LOW_ERROR');
                                break;
                            case 404:
                                errorMessage = this.translateService.instant('AI_CHAT.CONVERSATION_NOT_FOUND_ERROR');
                                break;
                            default:
                                errorMessage = this.translateService.instant('AI_CHAT.SEND_MESSAGE_ERROR');
                                break;
                        }
                    } else {
                        errorMessage = this.translateService.instant('AI_CHAT.SEND_MESSAGE_ERROR');
                    }

                    const markdownMessage = this.replaceDocumentNamesAndIds(errorMessage);
                    const formattedHtmlMessage = await this.parseMarkdown(markdownMessage);
                    generatedChatResponse.update(response => {
                        return {
                            ...response,
                            markdownMessage,
                            formattedHtmlMessage
                        };
                    });
                    this.updateAiChat({ isGeneratingMessage: false });
                }
            });
    }

    private async addFullMessageToChat(message: string, messageType: AiChatMessageTypeEnum): Promise<void> {
        const markdownMessage = this.replaceDocumentNamesAndIds(message);
        const formattedMessage = await this.parseMarkdown(markdownMessage);
        const chatMessage: WritableSignal<AiChatMessage> = signal({
            formattedHtmlMessage: formattedMessage,
            messageType: messageType,
            markdownMessage: markdownMessage,
        });

        this.aiChatSignal.update(chat => {
            chat.chatMessages.push(chatMessage);
            return chat;
        });
    }

    private getDocumentsWelcomeMessage(documentIds: string[]): string {
        let sourcesText = '';
        if (documentIds.length > 0) {
            sourcesText += '\n';

            for (const documentId of documentIds) {
                sourcesText += '\n- ' + documentId;
            }
        }

        return this.translateService.instant('AI_CHAT.DOCUMENT.FIRST_PROMPT') + sourcesText;
    }

    private replaceDocumentNamesAndIds(message: string): string {
        let regEx: RegExp;
        this.aiChatSignal()
            .chatDocuments
            .forEach((chatDocument: AiChatDocument) => {
                regEx = new RegExp(chatDocument.documentName, 'ig');
                message = message.replace(regEx, chatDocument.documentId);

                regEx = new RegExp(chatDocument.documentId, 'ig');
                message = message.replace(regEx, this.createMarkdownLinkForChatDocument(chatDocument));
            });

        return message;
    }

    private createMarkdownLinkForChatDocument(chatDocument: AiChatDocument): string {
        return '[' + chatDocument.documentName + '](' + environment.deepLinkPrefix + chatDocument.documentId + ')';
    }

    private resetChat(): void {
        this.updateAiChat({
            conversationName: '',
            aiApiHostUrl: '',
            conversationId: '',
            chatDocuments: [],
            promptSuggestions: [],
            chatMessages: [],
            isGeneratingMessage: false
        });
        this.currentChatVault = undefined;
    }

    private async parseMarkdown(message: string): Promise<string> {
        const renderer = new marked.Renderer();
        renderer.link = ({
                             href,
                             text
                         }): string => {
            if (this.aiChatSignal()
                .chatDocuments
                .some(document => href.includes(document.documentId))) {
                return '<a href="' + href + '">' + text + '</a>';
            }

            return text;
        };

        renderer.em = ({ text }): string => {
            return text;
        };

        return marked.parse(message,
            {
                renderer,
                breaks: true
            });
    }

    private async fetchCurrentChatDocuments(): Promise<void> {
        try {
            const aiConversationsGetDocumentsRoute = '/conversations/' + this.aiChatSignal().conversationId + '/documents';
            const headers = new HttpHeaders({
                'Authorization': await this.authService.getBearer()
            });
            const chatDocuments = (await lastValueFrom(this.httpClient.get(
                this.aiChatSignal().aiApiHostUrl + aiConversationsGetDocumentsRoute,
                {
                    headers
                }
            ))) as Array<ChatDocument>;

            const aiChatDocuments =
                chatDocuments.map((chatDocument: ChatDocument): AiChatDocument => {
                    return {
                        documentId: chatDocument.id!,
                        documentName: chatDocument.name!
                    };
                });

            this.updateAiChat({
                chatDocuments: aiChatDocuments
            });
        } catch (error) {
            this.sentryService.captureException(error, `Fetching chat documents for conversation ${this.aiChatSignal().conversationId} failed.`);
        }
    }

    private updateAiChat(partialAiChat: Partial<AiChat>): void {
        this.aiChatSignal.update(chat => {
            return { ...chat, ...partialAiChat };
        });
    }
}
